import { scanFiles, onScanFilesProgress, batchThumbnails, type BlockedLibrary } from './electron'
import type { NuxCluster } from './electron'

// --- Constants: all scan/filter/cluster config lives here ---

const MEDIA_EXTENSIONS = ['.mp4', '.mov', '.avi', '.mkv', '.webm', '.jpg', '.jpeg', '.png', '.gif', '.mp3', '.wav', '.aac', '.flac']
const IMAGE_EXTENSIONS = ['.jpg', '.jpeg', '.png', '.gif']
const VIDEO_EXTENSIONS = ['.mp4', '.mov', '.avi', '.mkv', '.webm']
const AUDIO_EXTENSIONS = ['.mp3', '.wav', '.aac', '.flac']

const SKIP_EXTENSIONS = ['.fcpbundle', '.fcpproject', '.app', '.bundle', '.plugin', '.kext', '.framework']

const JUNK_SEGMENTS = [
  'node_modules', 'cache', 'Cache', 'Caches', 'tmp', 'temp', '.Trash',
  'Icon', 'Thumbnails', 'AppData', 'Application Support',
  'Library/Application Support', '__pycache__', '.git', 'bower_components',
  '__Trash', '__Temp', 'Render Files',
]

const SCAN_DIRS = ['~/Movies', '~/Pictures', '~/Desktop']
const NLE_DIRS = ['~/Documents/Adobe/Premiere Pro', '~/Movies/Final Cut Backups']

const MIN_IMAGE_SIZE = 50_000
const MAX_CLUSTER_SIZE = 120
const MIN_CLUSTER_SIZE = 3
const MIN_GAP_MS = 24 * 60 * 60 * 1000 // 24 hours

// --- Types ---

export type ScannedFile = {
  path: string
  name: string
  ext: string
  size: number
  createdAt: string
  modifiedAt: string
}

type MediaFile = ScannedFile & {
  type: 'video' | 'image' | 'audio' | 'other'
  folder: string
  effectiveDate: string
}

function extractDateFromFilename(name: string): Date | null {
  // IMG_20240315_123456.jpg, DJI_20250408212748_0157.mov, VID_20230501_*.mp4
  const m1 = name.match(/(?:IMG|DJI|VID|MOV|DSC|PXL|MVIMG|SAVE)_?(20\d{2})(0[1-9]|1[0-2])(0[1-9]|[12]\d|3[01])/)
  if (m1) return new Date(parseInt(m1[1]), parseInt(m1[2]) - 1, parseInt(m1[3]))

  // Screenshot 2024-03-15 at 1.23.45 PM.png
  const m2 = name.match(/(?:Screenshot|Screen Shot|Screen Recording|LWScreenShot)\s+(20\d{2})[-. ](0[1-9]|1[0-2])[-. ](0[1-9]|[12]\d|3[01])/)
  if (m2) return new Date(parseInt(m2[1]), parseInt(m2[2]) - 1, parseInt(m2[3]))

  // VIDEO-2024-05-17-10-11-46.mov or 2024.08.21.11.26.33-*.png
  const m3 = name.match(/(?:^|VIDEO-|PHOTO-)(20\d{2})[-._](0[1-9]|1[0-2])[-._](0[1-9]|[12]\d|3[01])/)
  if (m3) return new Date(parseInt(m3[1]), parseInt(m3[2]) - 1, parseInt(m3[3]))

  // Screen Capture 1-5-24, 5.44.30 PM.mp4 (M-D-YY format)
  const m4 = name.match(/Screen Capture\s+(\d{1,2})-(\d{1,2})-(\d{2}),/)
  if (m4) {
    const yr = parseInt(m4[3]) + 2000
    if (yr >= 2020 && yr <= 2030) return new Date(yr, parseInt(m4[1]) - 1, parseInt(m4[2]))
  }

  return null
}

function extractDateFromPath(filePath: string): Date | null {
  // Try filename first (most specific)
  const lastSlash = filePath.lastIndexOf('/')
  const name = lastSlash >= 0 ? filePath.slice(lastSlash + 1) : filePath
  const fromName = extractDateFromFilename(name)
  if (fromName) return fromName

  // Try path segments (folder structure)
  const segments = filePath.split('/')
  for (const seg of segments) {
    const m = seg.match(/^(20\d{2})[-_](0[1-9]|1[0-2])[-_](0[1-9]|[12]\d|3[01])/)
    if (m) return new Date(parseInt(m[1]), parseInt(m[2]) - 1, parseInt(m[3]))
  }
  for (let i = 0; i < segments.length - 2; i++) {
    const y = parseInt(segments[i])
    const mo = parseInt(segments[i + 1])
    const d = parseInt(segments[i + 2])
    if (y >= 2000 && y <= 2099 && mo >= 1 && mo <= 12 && d >= 1 && d <= 31) {
      return new Date(y, mo - 1, d)
    }
  }
  for (let i = 0; i < segments.length - 1; i++) {
    const y = parseInt(segments[i])
    const mo = parseInt(segments[i + 1])
    if (y >= 2000 && y <= 2099 && segments[i].length === 4 && mo >= 1 && mo <= 12 && segments[i + 1].length === 2) {
      return new Date(y, mo - 1, 1)
    }
  }
  return null
}

function getEffectiveDate(f: ScannedFile, probedDate?: string): string {
  // Prefer ffprobe creation_time (actual media metadata) over filesystem dates
  if (probedDate) return probedDate

  const c = new Date(f.createdAt).getTime()
  const m = new Date(f.modifiedAt).getTime()
  const fsDate = (m > 0 && m < c) ? f.modifiedAt : f.createdAt

  // Try to extract a real date from folder path — more reliable than fs dates
  // for copied/synced/downloaded media
  const pathDate = extractDateFromPath(f.path)
  if (pathDate) {
    const fsTime = new Date(fsDate).getTime()
    const pathTime = pathDate.getTime()
    // Use path date if it's older than the fs date (likely the real creation date)
    if (pathTime < fsTime - 60_000) {
      return pathDate.toISOString()
    }
  }

  return fsDate
}

export type ScanProgress = {
  filesFound: number
  dirsScanned: number
  totalDirs: number
  currentDir: string
}

export type ScanResult = {
  files: number
  clusters: NuxCluster[]
  blockedLibraries: BlockedLibrary[]
}

// --- File filtering (applied in app layer to raw electron results) ---

const mediaSet = new Set(MEDIA_EXTENSIONS)
const skipExtSet = new Set(SKIP_EXTENSIONS)
const junkSet = new Set(JUNK_SEGMENTS)

function filterRawFiles(files: ScannedFile[]): ScannedFile[] {
  return files.filter(f => {
    if (!mediaSet.has(f.ext)) return false
    if (imageSet.has(f.ext) && f.size < MIN_IMAGE_SIZE) return false
    const segments = f.path.split('/')
    for (const seg of segments) {
      if (junkSet.has(seg)) return false
      const ext = seg.lastIndexOf('.') >= 0 ? seg.slice(seg.lastIndexOf('.')).toLowerCase() : ''
      if (skipExtSet.has(ext)) return false
    }
    return true
  })
}

// --- File classification ---

const videoSet = new Set(VIDEO_EXTENSIONS)
const imageSet = new Set(IMAGE_EXTENSIONS)
const audioSet = new Set(AUDIO_EXTENSIONS)

function classifyFile(f: ScannedFile, probedDates: Record<string, string>): MediaFile {
  const type = videoSet.has(f.ext) ? 'video' as const
    : imageSet.has(f.ext) ? 'image' as const
    : audioSet.has(f.ext) ? 'audio' as const
    : 'other' as const
  const lastSlash = f.path.lastIndexOf('/')
  return { ...f, type, folder: lastSlash > 0 ? f.path.slice(0, lastSlash) : '', effectiveDate: getEffectiveDate(f, probedDates[f.path]) }
}

// --- Clustering ---

function clusterFiles(files: MediaFile[]): NuxCluster[] {
  if (files.length === 0) return []

  const sorted = [...files].sort((a, b) => {
    return new Date(a.effectiveDate).getTime() - new Date(b.effectiveDate).getTime()
  })

  const gaps: number[] = []
  for (let i = 1; i < sorted.length; i++) {
    gaps.push(new Date(sorted[i].effectiveDate).getTime() - new Date(sorted[i - 1].effectiveDate).getTime())
  }

  // Use only significant gaps (> 2 hours) for adaptive calculation
  // to avoid being skewed by same-date files with tiny/zero gaps
  const significant = gaps.filter(g => g > 7_200_000).sort((a, b) => a - b)
  const p75 = significant.length > 0 ? significant[Math.floor(significant.length * 0.75)] : MIN_GAP_MS
  const adaptiveGap = Math.max(MIN_GAP_MS, p75)

  const rawGroups: MediaFile[][] = []
  let current = [sorted[0]]
  for (let i = 1; i < sorted.length; i++) {
    if (gaps[i - 1] > adaptiveGap) {
      rawGroups.push(current)
      current = [sorted[i]]
    } else {
      current.push(sorted[i])
    }
  }
  if (current.length > 0) rawGroups.push(current)

  // Merge small groups (< MIN_CLUSTER_SIZE) into nearest neighbor
  let didMerge = true
  while (didMerge) {
    didMerge = false
    for (let i = 0; i < rawGroups.length; i++) {
      if (rawGroups[i].length >= MIN_CLUSTER_SIZE || rawGroups.length <= 1) continue
      // Merge into adjacent group (prefer the one with fewer files to keep balance)
      const prev = i > 0 ? i - 1 : -1
      const next = i < rawGroups.length - 1 ? i + 1 : -1
      let target: number
      if (prev === -1) target = next
      else if (next === -1) target = prev
      else target = rawGroups[prev].length <= rawGroups[next].length ? prev : next
      if (target >= 0) {
        rawGroups[target].push(...rawGroups[i])
        rawGroups.splice(i, 1)
        didMerge = true
        break
      }
    }
  }

  const clusters: NuxCluster[] = []
  for (const group of rawGroups) {
    if (group.length <= MAX_CLUSTER_SIZE) {
      clusters.push(buildCluster(group))
    } else {
      splitGroup(group, clusters)
    }
  }

  clusters.sort((a, b) => {
    const da = a.latestDate ? new Date(a.latestDate).getTime() : 0
    const db = b.latestDate ? new Date(b.latestDate).getTime() : 0
    return db - da
  })

  return clusters
}

function splitGroup(group: MediaFile[], clusters: NuxCluster[]) {
  // Find the largest time gap in this group
  let maxGap = 0
  let splitIdx = -1
  for (let i = 1; i < group.length; i++) {
    const g = new Date(group[i].effectiveDate).getTime() - new Date(group[i - 1].effectiveDate).getTime()
    if (g > maxGap) { maxGap = g; splitIdx = i }
  }

  // If no meaningful gap exists (all same date), keep as one cluster regardless of size
  if (maxGap < MIN_GAP_MS) {
    clusters.push(buildCluster(group))
    return
  }

  // Split at the largest gap and recurse
  const left = group.slice(0, splitIdx)
  const right = group.slice(splitIdx)

  if (left.length <= MAX_CLUSTER_SIZE) clusters.push(buildCluster(left))
  else splitGroup(left, clusters)

  if (right.length <= MAX_CLUSTER_SIZE) clusters.push(buildCluster(right))
  else splitGroup(right, clusters)
}

function buildCluster(files: MediaFile[]): NuxCluster {
  const dates = files
    .map(f => new Date(f.effectiveDate))
    .filter(d => !isNaN(d.getTime()))
    .sort((a, b) => a.getTime() - b.getTime())

  const earliest = dates[0]
  const latest = dates[dates.length - 1]

  const fmt = (d: Date) => d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
  let dateRange = ''
  if (earliest && latest) {
    dateRange = earliest.toDateString() === latest.toDateString()
      ? fmt(earliest)
      : `${fmt(earliest)} - ${fmt(latest)}`
  }

  const folders = new Set(files.map(f => f.folder))
  const folderPath = [...folders][0]
  const folderParts = folderPath?.split('/').filter(Boolean) ?? []
  const folderName = folders.size === 1
    ? (folderParts.pop() ?? '')
    : `${folders.size} folders`
  const types = new Set(files.map(f => f.type).filter(t => t !== 'other'))

  const genericNames = new Set(['old', 'media', 'files', 'images', 'videos', 'photos', 'originals', 'original media', 'exports', 'output', 'downloads', 'new', 'misc', 'other', 'backup', 'stuff', 'render files', 'renders'])
  const isGenericFolder = folders.size === 1 && genericNames.has(folderName.toLowerCase())
  let label: string
  if (folders.size > 1 || isGenericFolder) {
    label = earliest
      ? earliest.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: earliest.getFullYear() !== new Date().getFullYear() ? 'numeric' : undefined })
      : folderName
  } else {
    label = folderName
  }

  const displayFolder = isGenericFolder && folderParts.length >= 1
    ? `${folderParts[folderParts.length - 1]}/${folderName}`
    : folderName

  const hash = simpleHash((earliest?.toISOString() || '') + (latest?.toISOString() || '') + files.length)

  return {
    id: `cluster_${hash}`,
    label,
    folder_name: displayFolder,
    folder_path: [...folders][0],
    file_count: files.length,
    date_range: dateRange,
    earliestDate: earliest?.toISOString(),
    latestDate: latest?.toISOString(),
    media_types: Array.from(types),
    files: files.map(f => ({ path: f.path, name: f.name, type: f.type, size: f.size })),
  }
}

function simpleHash(input: string): string {
  let h = 0
  for (let i = 0; i < input.length; i++) {
    h = ((h << 5) - h + input.charCodeAt(i)) | 0
  }
  return Math.abs(h).toString(36).slice(0, 12)
}

// --- Main scan orchestrator ---

export async function runNuxScan(
  onProgress?: (p: ScanProgress) => void,
): Promise<ScanResult> {
  const t0 = performance.now()
  const dirs = [...SCAN_DIRS, ...NLE_DIRS]

  const unsub = onProgress ? onScanFilesProgress(onProgress) : null

  const result = await scanFiles(dirs, {
    skipDirExts: SKIP_EXTENSIONS,
    skipDirNames: JUNK_SEGMENTS,
    fileExts: MEDIA_EXTENSIONS,
  })
  unsub?.()

  if (!result) {
    console.warn('[media-scan] scanFiles returned null')
    return { files: 0, clusters: [], blockedLibraries: [] }
  }

  const filtered = filterRawFiles(result.files)
  const mediaFiles = filtered.map((f): MediaFile => classifyFile(f, {}))
  const clusters = clusterFiles(mediaFiles)
  console.log(`[media-scan] ${result.files.length} raw -> ${filtered.length} filtered -> ${clusters.length} clusters in ${(performance.now() - t0).toFixed(0)}ms`)

  const blockedLibraries: BlockedLibrary[] = (result.blockedPaths || [])
    .filter((bp: { path: string; code: string }) => {
      const ext = bp.path.split('.').pop()?.toLowerCase()
      return ext === 'photoslibrary' || ext === 'photolibrary'
    })
    .map((bp: { path: string }) => ({ type: 'photos' as const, path: bp.path }))

  return { files: mediaFiles.length, clusters, blockedLibraries }
}

export async function extractClusterThumbnails(
  clusters: NuxCluster[],
  onThumbnail: (clusterId: string, dataUrl: string) => void,
) {
  const t0 = performance.now()
  let extracted = 0
  const BATCH = 2
  for (let i = 0; i < clusters.length; i += BATCH) {
    // Yield to main thread between batches so UI stays responsive (one frame)
    await new Promise(r => setTimeout(r, 16))

    const batch = clusters.slice(i, i + BATCH)

    const imageBatch: { clusterId: string; path: string }[] = []
    const videoBatch: { clusterId: string; path: string }[] = []

    for (const c of batch) {
      const imgFile = c.files.find(f => f.type === 'image')
      if (imgFile) {
        imageBatch.push({ clusterId: c.id, path: imgFile.path })
      } else {
        const vidFile = c.files.find(f => f.type === 'video')
        if (vidFile) videoBatch.push({ clusterId: c.id, path: vidFile.path })
      }
    }

    const empty: Record<string, string | undefined> = {}
    const [imgResults, vidResults] = await Promise.all([
      imageBatch.length > 0 ? batchThumbnails(imageBatch.map(f => f.path), 320) : Promise.resolve(empty),
      videoBatch.length > 0 ? batchThumbnails(videoBatch.map(f => f.path), 320, 1) : Promise.resolve(empty),
    ])

    for (const { clusterId, path } of imageBatch) {
      if (imgResults[path]) onThumbnail(clusterId, imgResults[path]!)
    }
    for (const { clusterId, path } of videoBatch) {
      if (vidResults[path]) onThumbnail(clusterId, vidResults[path]!)
    }
    extracted += imageBatch.length + videoBatch.length
  }
  console.log(`[media-scan] thumbnails: ${extracted} clusters in ${(performance.now() - t0).toFixed(0)}ms`)
}
